// Compute the next upcoming Mass for a given GKK by scanning the parish calendar.
import { calendar } from "@/components/ParishCalendar";

const WEEKDAY_SHORT = {
  Sunday: "Sun",
  Monday: "Mon",
  Tuesday: "Tue",
  Wednesday: "Wed",
  Thursday: "Thu",
  Friday: "Fri",
  Saturday: "Sat",
};

function normalize(s) {
  return s
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

function parseDate(label) {
  // Labels are like "July 7, 2026"
  const d = new Date(label);
  d.setHours(0, 0, 0, 0);
  return d;
}

function todayInManila() {
  // Use Manila timezone to determine "today" so the directory stays in sync with
  // the parish's reality even when the visitor is abroad.
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: "Asia/Manila",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date());
  const get = (t) => parts.find((p) => p.type === t)?.value;
  const d = new Date(`${get("year")}-${get("month")}-${get("day")}T00:00:00`);
  return d;
}

/**
 * Find the next future Mass for a GKK.
 * Matching strategy: the calendar entry's venue must contain BOTH a meaningful
 * patron token (>3 chars) AND the GKK's venue label (or a major venue token).
 */
export function nextMassForGKK(gkk) {
  const today = todayInManila();
  const sorted = [...calendar].sort(
    (a, b) => parseDate(a.date) - parseDate(b.date)
  );

  const venueNorm = normalize(gkk.venue);
  const venueTokens = venueNorm.split(" ").filter((w) => w.length > 2);
  const patronTokens = normalize(gkk.patron)
    .split(" ")
    .filter((w) => w.length > 3 && w !== "lady" && w !== "saint");

  for (const entry of sorted) {
    const entryDate = parseDate(entry.date);
    if (entryDate < today) continue;
    for (const ev of entry.events || []) {
      if (!ev || !ev.v) continue;
      const v = normalize(ev.v);
      if (!v.startsWith("gkk")) continue; // we only want GKK Masses

      // Venue must overlap (full substring OR all major tokens present)
      const venueMatch =
        (venueNorm && v.includes(venueNorm)) ||
        (venueTokens.length > 0 && venueTokens.every((t) => v.includes(t)));
      if (!venueMatch) continue;

      // Patron must overlap (at least one significant token), if any.
      const patronMatch =
        patronTokens.length === 0 ||
        patronTokens.some((t) => v.includes(t));
      if (!patronMatch) continue;

      return {
        date: entryDate,
        weekdayShort: WEEKDAY_SHORT[entry.day] || entry.day,
        time: ev.t,
        event: ev.e,
      };
    }
  }
  return null;
}

// Parse a time string like "5:30 PM" into a sortable numeric value (minutes since midnight)
function parseTimeToMinutes(t) {
  if (!t) return Number.MAX_SAFE_INTEGER;
  const m = t.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
  if (!m) return Number.MAX_SAFE_INTEGER;
  let h = parseInt(m[1], 10);
  const min = parseInt(m[2], 10);
  const ampm = m[3].toUpperCase();
  if (ampm === "PM" && h !== 12) h += 12;
  if (ampm === "AM" && h === 12) h = 0;
  return h * 60 + min;
}

/**
 * Return ALL GKK Masses celebrated TODAY (Manila), sorted by time.
 * Each row: { time, patron, venue, event, sortValue }
 */
export function todaysGkkMasses() {
  const today = todayInManila();
  const entry = calendar.find((e) => {
    const d = parseDate(e.date);
    return (
      d.getFullYear() === today.getFullYear() &&
      d.getMonth() === today.getMonth() &&
      d.getDate() === today.getDate()
    );
  });
  if (!entry) return [];
  const rows = [];
  for (const ev of entry.events || []) {
    if (!ev || !ev.v) continue;
    const v = ev.v.trim();
    if (!/^GKK\b/i.test(v)) continue; // only GKK Masses
    // Split "GKK Patron Saint, Venue Description" → patron + venue
    const stripped = v.replace(/^GKK\s+/i, "");
    let patron = stripped;
    let venue = "";
    // Prefer comma split; some entries use "(…)"
    const commaIdx = stripped.indexOf(",");
    const parenMatch = stripped.match(/^([^()]+?)\s*\(([^)]+)\)\s*$/);
    if (commaIdx !== -1) {
      patron = stripped.slice(0, commaIdx).trim();
      venue = stripped.slice(commaIdx + 1).trim();
    } else if (parenMatch) {
      patron = parenMatch[1].trim();
      venue = parenMatch[2].trim();
    }
    rows.push({
      time: ev.t,
      patron,
      venue,
      event: ev.e,
      sortValue: parseTimeToMinutes(ev.t),
    });
  }
  return rows.sort((a, b) => a.sortValue - b.sortValue);
}
